import { useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  UserPlus,
  Plus,
  Search,
  Code,
  Lightbulb,
  Palette,
  TrendingUp,
  User,
  BarChart3,
  Heart,
  AlertTriangle,
} from "lucide-react";
import { useTeams } from "@/hooks/api-hooks";
import { useAuth } from "@/contexts/auth-context";
import { EnhancedStatCard } from "@/components/ui/EnhancedStatCard";
import { EnhancedTeamCard } from "@/components/ui/EnhancedTeamCard";
import { ProminentUsageBanner } from "@/components/ui/ProminentUsageBanner";
import { getAvatarColor } from "@/lib/utils";

export function TeamPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("all");

  // Check if user has access to team management
  const canManageTeams = user?.organizationRole === 'owner' || user?.organizationRole === 'admin';

  // Mock usage data for now (can be replaced with real API)
  const usageData = {
    teamUsage: {
      current: 3,
      limit: 10,
      percentage: 30,
      limitReached: false
    }
  };

  const canPerformAction = () => canManageTeams && !usageData.teamUsage.limitReached;
  const showLimitReachedModal = (action: string) => {
    console.log(`Limit reached for ${action}`);
  };
  const refreshUsageData = () => {
    console.log('Refreshing usage data...');
  };

  // Early return if user doesn't have access - show 403 page
  if (user && !canManageTeams) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto">
          <div className="mx-auto w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mb-6">
            <Users className="w-12 h-12 text-orange-600" />
          </div>
          <div className="text-6xl font-bold text-muted-foreground mb-4">403</div>
          <h2 className="text-2xl font-bold mb-4">Access Forbidden</h2>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access team management. This feature is only available to administrators and owners.
          </p>
          <div className="space-y-3">
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Return to Dashboard
            </Button>
            <Button onClick={() => navigate(-1)} variant="outline" className="w-full">
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Use API hooks for real data
  const { data: teamsData, isLoading: teamsLoading, error: teamsError } = useTeams({ includeMembers: true });

  const rawTeams: any[] = ((teamsData as { teams?: any[] })?.teams) ?? [];

  // Mock analytics data (can be replaced with real API)
  const analytics = {
    overview: {
      totalTeams: rawTeams.length,
      totalMembers: rawTeams.reduce((sum: number, team: any) => sum + (team.memberCount || 0), 0),
      teamLeaders: rawTeams.filter((team: any) => team.manager).length,
      temperature: "7.7"
    }
  };

  // Debug logging
  console.log('[TeamPage] Debug:', {
    user,
    userRole: user?.role,
    organizationRole: user?.organizationRole,
    teamsLoading,
    teamsError,
    teamsData,
    rawTeams,
    analytics
  });

  const getMemberUser = (member: any) => member?.user ?? member;

  const getMemberDisplayName = (member: any) => {
    const user = getMemberUser(member);
    const fullName = user?.fullName || user?.name;
    if (fullName && fullName.trim().length > 0) {
      return fullName;
    }

    const firstName = user?.firstName || user?.first_name;
    const lastName = user?.lastName || user?.last_name;
    if (firstName || lastName) {
      return `${firstName ?? ''} ${lastName ?? ''}`.trim();
    }

    if (typeof user?.email === 'string' && user.email.length > 0) {
      return user.email.split('@')[0];
    }

    return 'Team member';
  };

  const getMemberInitials = (member: any) => {
    const displayName = getMemberDisplayName(member);
    if (displayName && displayName.length > 0 && displayName !== 'Team member') {
      const parts = displayName.trim().split(/\s+/);
      const initials = parts.slice(0, 2).map((part: string) => part[0] ?? '').join('');
      if (initials.trim().length > 0) {
        return initials.toUpperCase();
      }
    }

    const user = getMemberUser(member);
    if (typeof user?.email === 'string' && user.email.length > 0) {
      return user.email.charAt(0).toUpperCase();
    }

    return '?';
  };

  // Transform API data to match expected format for EnhancedTeamCard
  const teams = rawTeams.map((team: any) => {
    // Get member avatars (first 3 members)
    const memberAvatars = (team.members || []).slice(0, 3).map((member: any, index: number) => {
      const displayName = getMemberDisplayName(member);
      const initials = getMemberInitials(member);
      const memberId = member.id || getMemberUser(member)?.id || `${team.id}-member-${index}`;
      const userData = getMemberUser(member) ?? {};
      const firstName = userData.firstName ?? userData.first_name ?? displayName.split(' ')[0] ?? '';
      const lastName = userData.lastName ?? userData.last_name ?? displayName.split(' ').slice(1).join(' ') ?? '';
      const avatarUrl = userData.avatar ?? member.avatar ?? '';
      return {
        id: memberId,
        name: initials,
        color: getAvatarColor(firstName, lastName),
        title: displayName,
        avatar: avatarUrl,
      };
    });

    return {
      id: team.id,
      name: team.name,
      description: team.description || `${team.name} Team`,
      icon: getTeamIcon(team.name),
      iconColor: "text-white",
      iconBg: "bg-primary",
      members: team.memberCount || 0,
      lead: team.manager?.name || "No Lead",
      performance: Math.floor(Math.random() * 100), // Mock performance data
      memberAvatars,
      additionalMembers: Math.max(0, (team.memberCount || 0) - 3),
    };
  });

  // Helper function to get appropriate icon based on team name
  function getTeamIcon(teamName: string) {
    const name = teamName.toLowerCase();
    if (name.includes('engineering') || name.includes('development') || name.includes('tech')) return Code;
    if (name.includes('product') || name.includes('strategy')) return Lightbulb;
    if (name.includes('design') || name.includes('ui') || name.includes('ux')) return Palette;
    if (name.includes('marketing') || name.includes('growth')) return TrendingUp;
    if (name.includes('sales') || name.includes('customer')) return User;
    if (name.includes('data') || name.includes('analytics')) return BarChart3;
    return Users; // Default icon
  }

  const filteredTeams = teams.filter((team: any) => {
    const matchesSearch = team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (team.description && team.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesDepartment = selectedDepartment === "all" ||
                             team.name.toLowerCase().includes(selectedDepartment.toLowerCase());
    return matchesSearch && matchesDepartment;
  });

  // Show loading state
  if (teamsLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="space-y-10 p-8 max-w-7xl mx-auto">
          <div className="flex items-center justify-between border-b border-gray-200/60 pb-8">
            <div>
              <h1 className="text-4xl font-bold text-foreground tracking-tight mb-3 leading-tight">Team Management</h1>
              <p className="text-lg text-foreground-secondary leading-relaxed">Loading team data...</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-card border border-border shadow-sm p-6 rounded-2xl animate-pulse">
                <div className="w-16 h-16 bg-surface rounded-xl mx-auto mb-4"></div>
                <div className="h-8 bg-surface rounded w-16 mx-auto mb-2"></div>
                <div className="h-4 bg-surface rounded w-24 mx-auto"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="space-y-10 p-8 max-w-7xl mx-auto">
        {/* Enhanced Header */}
        <div className="flex items-center justify-between border-b border-border pb-8">
          <div>
            <h1 className="text-4xl font-bold text-foreground tracking-tight mb-3 leading-tight">Team Management</h1>
            <p className="text-lg text-foreground-secondary leading-relaxed max-w-2xl">
              Manage teams, assign members, and track performance across your organization
            </p>
            {/* Enhanced Team Usage Display */}
            {usageData && (
              <div className="flex items-center gap-4 mt-6">
                <div className="text-sm text-foreground-secondary bg-surface/80 px-3 py-2 rounded-lg border border-border backdrop-blur-sm">
                  <span className="font-semibold text-foreground">{usageData.teamUsage.current} of {usageData.teamUsage.limit}</span> teams
                  <span className="ml-2 text-xs text-muted-foreground">({usageData.teamUsage.percentage}%)</span>
                </div>
                {usageData.teamUsage.limitReached && (
                  <Badge className="text-xs border-0 bg-red-50 text-red-700 border border-red-200/60">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Limit Reached
                  </Badge>
                )}
                <Button
                  onClick={refreshUsageData}
                  size="sm"
                  variant="outline"
                  className="text-xs px-3 py-2 h-8 rounded-lg border-border hover:border-border-strong"
                  title="Refresh usage data"
                >
                  ↻
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <Link to="/team/create">
              <Button
                variant="outline"
                className="gap-2 rounded-xl px-5 py-2.5 h-auto font-medium border-border hover:border-border-strong hover:bg-surface/50 transition-all duration-200"
                disabled={!canPerformAction()}
                onClick={(e) => {
                  if (!canPerformAction()) {
                    e.preventDefault();
                    showLimitReachedModal('team');
                  }
                }}
              >
                <Plus className="h-4 w-4" />
                Create Team
              </Button>
            </Link>
            <Link to="/members">
              <Button className="gap-2">
                <UserPlus className="h-4 w-4" />
                Add Member
              </Button>
            </Link>
          </div>
        </div>

        {/* Enhanced Team Usage Banner */}
        <ProminentUsageBanner
          actionType="team"
          className="mb-8"
          dismissible={true}
          onDismiss={() => console.log('Banner dismissed')}
        />

        {/* Enhanced Organization Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <EnhancedStatCard
            title="Total Teams"
            value={analytics?.overview?.totalTeams || 0}
            icon={Users}
            colorScheme="primary"
            trend={{
              value: 12,
              label: "vs last month",
              isPositive: true
            }}
          />

          <EnhancedStatCard
            title="Total Members"
            value={analytics?.overview?.totalMembers || 0}
            icon={Users}
            colorScheme="success"
            trend={{
              value: 8,
              label: "vs last month",
              isPositive: true
            }}
          />

          <EnhancedStatCard
            title="Team Leaders"
            value={analytics?.overview?.teamLeaders || 0}
            icon={User}
            colorScheme="warning"
            trend={{
              value: 5,
              label: "vs last month",
              isPositive: true
            }}
          />

          <EnhancedStatCard
            title="Temperature"
            value={analytics?.overview?.temperature || "7.7"}
            icon={Heart}
            colorScheme="neutral"
            trend={{
              value: 2,
              label: "vs last month",
              isPositive: true
            }}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Teams Grid */}
          <div className="lg:col-span-2">
            <div className="bg-card border border-border shadow-sm hover:shadow-md p-8 rounded-2xl mb-8 transition-all duration-200">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <h2 className="text-2xl font-bold text-foreground tracking-tight">Teams</h2>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
                    <Input
                      placeholder="Search teams..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-12 w-72 h-12 rounded-xl border border-border focus:border-primary/50 focus:ring-2 focus:ring-primary/20 bg-input backdrop-blur-sm transition-all duration-200"
                    />
                  </div>
                  <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                    <SelectTrigger className="w-[180px] h-12 rounded-xl border border-border bg-input backdrop-blur-sm hover:bg-surface transition-all duration-200">
                      <SelectValue placeholder="All Departments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {teams.map((team: any) => (
                        <SelectItem key={team.id} value={team.name.toLowerCase()}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {filteredTeams.length > 0 ? filteredTeams.map((team) => (
                <EnhancedTeamCard key={team.id} team={team} />
              )) : (
                <div className="col-span-2 text-center py-16">
                  <div className="w-24 h-24 bg-gradient-to-br from-surface to-surface-variant border border-border rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-sm">
                    <Users className="h-12 w-12 text-muted-foreground" />
                  </div>
                  <h3 className="text-3xl font-bold text-foreground mb-4 tracking-tight">No teams found</h3>
                  <p className="text-foreground-secondary text-lg mb-8 max-w-md mx-auto leading-relaxed">
                    {searchTerm || selectedDepartment !== "all"
                      ? "Try adjusting your search or filters to find teams."
                      : "Start by creating your first team to organize your organization."
                    }
                  </p>
                  <Link to="/team/create">
                    <Button
                      className="bg-gradient-to-r from-primary to-primary-hover hover:from-primary-hover hover:to-primary text-primary-foreground rounded-xl px-6 py-3 h-auto font-medium shadow-sm hover:shadow-md transition-all duration-200"
                      disabled={!canPerformAction()}
                      onClick={(e) => {
                        if (!canPerformAction()) {
                          e.preventDefault();
                          showLimitReachedModal('team');
                        }
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Team
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions Sidebar */}
          <div className="space-y-8">
            <div className="bg-card border border-border shadow-sm hover:shadow-md p-8 rounded-2xl transition-all duration-200">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 bg-gradient-to-br from-primary/10 to-primary/20 border border-primary/20 rounded-xl flex items-center justify-center shadow-sm">
                  <Plus className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground tracking-tight">Quick Actions</h3>
              </div>
              <div className="space-y-4">
                <Link to="/team/create">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-3 h-12 rounded-xl font-medium border-border hover:border-border-strong hover:bg-surface/50 transition-all duration-200"
                    disabled={!canPerformAction()}
                    onClick={(e) => {
                      if (!canPerformAction()) {
                        e.preventDefault();
                        showLimitReachedModal('team');
                      }
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Create New Team
                  </Button>
                </Link>
                <Link to="/members">
                  <Button variant="outline" className="w-full justify-start gap-3 h-12 rounded-xl font-medium border-border hover:border-border-strong hover:bg-surface/50 transition-all duration-200">
                    <UserPlus className="h-4 w-4" />
                    Add Team Member
                  </Button>
                </Link>
                <Link to="/team/analytics">
                  <Button variant="outline" className="w-full justify-start gap-3 h-12 rounded-xl font-medium border-border hover:border-border-strong hover:bg-surface/50 transition-all duration-200">
                    <BarChart3 className="h-4 w-4" />
                    View Analytics
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
